import SelectScreenContentDialog from "@components/screens/SelectScreenContentDialog";
import { Button } from "@components/ui/button";
import { useAuth } from "@hooks/useAuth";
import { useDataMutation } from "@hooks/useData/useDataMutation";
import { useDataQuery } from "@hooks/useData/useDataQuery";
import useDialog from "@hooks/useDialog";
import { gzLog } from "@hooks/useGzLog";
import { useLocations } from "@hooks/useLocations";
import { useGlobalMusic } from "@hooks/useMusic";
import { useUserSubscriptions } from "@hooks/useUserSubscriptions";
import { env } from "@src/env.mjs";
import CustomWebSocket from "@utils/customWebSocket";
import { useUpdateScreenPlaying } from "@utils/queries";
import { authClient } from "@utils/authClient";
import { selectData, supabase } from "@utils/serverClient";
import Link from "next/link";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Json } from "types/schema";
import { Screen, ScreenReturnProps } from "./types";
import { useGlobalScreenshot } from "@components/screens/hooks/useGlobalScreenshot";
import { useQueryClient } from "@tanstack/react-query";

const screensContext = createContext<ScreenReturnProps | null>(null);
const { Provider } = screensContext;
type Props = {
  children: React.ReactNode;
};

export const ScreenWithPlayingNameQuery =
  "*, layout (name), playlists (name, items), asset (name), template(name)";

export type ScreenWithPlayingInfo = Screen & {
  layout: { name: string | null } | null;
  playlists: {
    name: string | null;
    items: Json | null;
  } | null;
  template: { name: string | null } | null;
  asset: { name: string | null } | null;
};
export function ScreensProvider(props: Props) {
  const { organization } = useAuth();
  if (!organization?.id) return null
  return <ScreensProviderImpl {...props} />
}

function ScreensProviderImpl({ children }: Props) {
  const { showErrorSnackbar, openDialog } = useDialog()

  const { user, organization } = useAuth();

  const orgId = organization?.id

  // The org is passed so the server can route this connection to a per-org
  // Durable Object instead of the single shared one. It is ignored unless
  // SOCKET_SHARDING is enabled server-side, so sending it is always safe — the
  // client can ship well ahead of the flag being flipped.
  //
  // Unlike the player, orgId resolves asynchronously here, so the socket is
  // rebuilt once it arrives. That lands before any rooms are joined (joinRooms
  // waits on the screens query, which itself needs orgId), so nothing is lost.
  const socket = useMemo(
    () =>
      CustomWebSocket.getInstance(
        env.NEXT_PUBLIC_WS_URL +
        "/" +
        env.NEXT_PUBLIC_WS_AUTHENTICATED_SCREEN_NAMESPACE +
        "/public/websocket" +
        (orgId ? `?orgId=${encodeURIComponent(orgId)}` : ""),
      ),
    [orgId],
  );
  const queryKey = ["screens", orgId]
  const {
    status,
    data: screens = [],
    refetch: getScreens,
  } = useDataQuery({
    queryKey,
    queryFn: async () => {
      if (!orgId) return [];
      const accessToken = await authClient.getAccessToken();

      if (!accessToken) return [];
      const { data, error } = await selectData({
        table: "screen",
        columns: ScreenWithPlayingNameQuery,
      })
        .eq("org_id", orgId)
        .order("created_at", { ascending: false })
        .returns<ScreenWithPlayingInfo[]>();
      if (error || !data) return [];
      return data;
    },
  });
  const { updatePlayingState, updatePlayingStates } = useGlobalMusic()
  const { updateScreenshotState, updateScreenshotStates } = useGlobalScreenshot()

  const screensRef = useRef(screens);
  screensRef.current = screens;

  useEffect(() => {
    if (status !== "success") return;
    const states = screens.reduce((a, v) => ({ ...a, [v.id]: { state: /* playingStates[v.id]?.state === "loading" ? "loading" : */ v.music_playing ? "playing" : "paused", playlistId: v.last_music_id } }), {})
    updatePlayingStates(states)
  }, [screens])

  const { mutate: updateScreenPlaying } = useUpdateScreenPlaying(
    user?.id,
    emitEvent,
  );

  const { planProperties } = useUserSubscriptions();

  const { mutate: updateScreen } = useDataMutation({
    mutationFn: async ({
      screenId,
      data,
    }: {
      screenId: string;
      data: Partial<ScreenWithPlayingInfo>;
    }) => {
      // Check if the update includes a location change and validate screen limits
      if (planProperties.max_screens_per_location && data.location_id) {
        const currentScreen = screens.find(s => s.id === screenId);

        // If the location is changing or being set for the first time
        if (currentScreen?.location_id !== data.location_id) {
          // Check if the new location already has max screens
          const existingScreens = screens.filter(s => s.location_id === data.location_id && s.id !== screenId);

          if (existingScreens && existingScreens.length >= planProperties.max_screens_per_location) {
            throw new Error(`You can only have ${planProperties.max_screens_per_location} screen(s) per location in your current plan. Please upgrade your plan to assign more screens to this location.`);
          }
        }
      }

      const { data: updatedScreen, error } = await supabase
        .from("screen")
        .update(data)
        .eq("id", screenId)
        .select(ScreenWithPlayingNameQuery)
        .returns<ScreenWithPlayingInfo[]>();

      if (error) throw error;
      return updatedScreen[0];
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to update screen",
    optimisticUpdateFn: (oldData: ScreenWithPlayingInfo[] | undefined, { screenId, data }) => {
      if (!oldData) return oldData;
      return oldData.map(
        screen => screen.id === screenId ? { ...screen, ...data } : screen
      );
    },
    options: {
      onSuccess(s, variables) {
        if (!s) return
        if ("music_integration_enabled" in variables.data && s.music_integration_enabled) {
          emitEvent("musicEnabled", s.id)
        }
        if ("group_id" in variables.data && !!s.group_id) {
          emitEvent("musicSyncEnabled", s.id)
        }
        if ("group_id" in variables.data && !s.group_id) {
          emitEvent("musicSyncDisabled", s.id)
        }
      },
      onError: (error) => {
        // If the error is about location limits, show an upgrade dialog
        if (error.message && error.message.includes("You can only have")) {
          openDialog({
            title: "Screen Limit Reached",
            customContent: (
              <div className="flex flex-col gap-2 pt-2">
                <p>
                  {error.message}
                </p>

                <div className="flex w-full items-center justify-end">
                  <Button asChild>
                    <Link href={"/change_plan"}>Upgrade Options</Link>
                  </Button>
                </div>
              </div>
            ),
          });
        }
      },
    }
  })

  const queryClient = useQueryClient();

  const joinRooms = useCallback(
    (screens: ScreenWithPlayingInfo[]) => {
      socket.emit(
        "joinRooms",
        { roomIds: screens.map((e) => e.id), id: "dashboard" },
        (data) => {
          const active = data?.activeScreens ?? [];
          const _dat = active.reduce((a, v) => ({ ...a, [v]: true }), {});
          setOnlineStatus(_dat);
        },
      );
    },
    [socket],
  );

  // A counter rather than a boolean, so that every open re-runs the join effect.
  // With a boolean, a reconnect set it to `true` when it was already `true`,
  // React bailed out, and the dashboard sat connected but joined to no rooms —
  // receiving no further joinedRoom/leftRoom and showing stale status until the
  // page was reloaded.
  const [connectionEpoch, setConnectionEpoch] = useState(0);

  useEffect(() => {
    if (connectionEpoch > 0 && screens.length > 0) {
      joinRooms(screens);
    }
  }, [connectionEpoch, screens, joinRooms]);

  const [onlineStatus, setOnlineStatus] = useState<Record<string, boolean>>({});

  useEffect(() => {
    socket.onOpen = () => {
      setConnectionEpoch((n) => n + 1);
    };

    // The socket may already be open by the time this effect runs (a cached
    // instance from getInstance, or a fast handshake), in which case onOpen has
    // already fired and would never fire again.
    if (socket.isOpen) setConnectionEpoch((n) => n + 1);

    return () => {
      socket.close();
    };
  }, [socket]);

  useEffect(() => {
    socket.onMessage = (message) => {
      try {
        const data = JSON.parse(message.data);
        const event = data.event;
        const groupId = data.data?.data?.groupId;

        switch (event) {
          case "joinedRoom":
            if (data.data.id) {
              setOnlineStatus((status) => ({
                ...status,
                [data.data.id]: true,
              }));
            }
            break;
          case "leftRoom": {
            if (data.data.id) {
              setOnlineStatus((status) => ({
                ...status,
                [data.data.id]: false,
              }));

              gzLog({
                eventtype: "JourneyTrack",
                name: "LeftRoom",
                payload: {
                  roomId: data.data.id,
                }
              })
            }
            break;
          }
          case "broadcastMessage": {
            const screenId = data.data.from;
            if (screenId === "dashboard") break;
            if (data.data.data.from !== "player") break;
            const content = data.data.content;
            switch (content) {
              case "musicResumed":
                updatePlayingState(screenId, { state: "playing" });
                break;
              case "musicPaused":
                updatePlayingState(screenId, { state: "paused" });
                break;
              case "groupMusicResumed": {
                const screensInGroup = screensRef.current.filter(s => s.group_id === groupId);
                for (const screen of screensInGroup) {
                  updatePlayingState(screen.id, { state: "playing" })
                }
                break;
              }
              case "groupMusicPaused": {
                const screensInGroup = screensRef.current.filter(s => s.group_id === groupId);
                for (const screen of screensInGroup) {
                  updatePlayingState(screen.id, { state: "paused" })
                }
                break;
              }
              case "musicError":
                if (!groupId) {
                  updatePlayingState(screenId, { state: "paused" });
                } else {
                  const screensInGroup = screensRef.current.filter(s => s.group_id === groupId);
                  for (const screen of screensInGroup) {
                    updatePlayingState(screen.id, { state: "paused" })
                  }
                }
                showErrorSnackbar(data.data.data?.error ?? "Something went wrong while playing music")
                break;
              case "screenshotUploaded":
                // Handle the screenshot upload completion
                handleScreenshotUploaded(screenId);
                queryClient.invalidateQueries({ queryKey: ["screenshots", screenId] });
                break;
            }
            break;
          }
        }
      } catch { }
    };

    return () => {
      socket.onMessage = null;
    };
  }, [socket]);

  // State to store screenshots for each screen
  // const [screenScreenshots, setScreenScreenshots] = useState<Record<string, { assetId: string, url: string, timestamp: number }>>({});

  // Function to handle screenshot upload completion
  const handleScreenshotUploaded = (screenId: string) => {
    updateScreenshotState(screenId, { state: "complete" });
  };

  function emitEvent(
    event: string,
    screenId: string,
    data?: Record<string, any>,
    callback?: (data?: object) => void,
  ) {
    try {
      const body = { roomId: screenId, messageContent: event, messageData: data }
      socket.emit(
        "broadcastMessage",
        body,
        callback,
      );
    } catch (e) {
      console.error('Error broadcasting message', e)
    }
  }

  const { locations } = useLocations()
  const assignedLocationIds = locations.map(l => l.id)

  return (
    <Provider
      value={{
        screens: screens.filter(s => !s.location_id || assignedLocationIds?.includes(s.location_id)),
        screensFetchStatus: status,
        getScreens,
        updateScreenPlaying,
        updateScreen,
        onlineStatus,
        emitEvent,
        // screenScreenshots,
        handleScreenshotUploaded, // This can be used to manually trigger screenshot handling if needed
      }}
    >
      {children}
    </Provider>
  );
}

export function useContentSelectorDialog() {
  const { openDialog, closeDialog } = useDialog()
  const { organization } = useAuth()
  const { updateScreenPlaying } = useScreens()

  function openContentSelectorDialog(screen: ScreenWithPlayingInfo) {
    if (!organization?.id) return
    openDialog({
      title: "What would you like to display in your signage?",
      showActions: false,
      height: "80vh",
      maxWidth: "60vw",
      customContent: <SelectScreenContentDialog
        screen={screen}
        orgId={organization.id}
        updateScreenPlaying={updateScreenPlaying}
        onClose={() => {
          closeDialog()
        }}
      />
    })
  }

  return { openContentSelectorDialog, closeContentSelectorDialog: closeDialog }
}

export const useScreens: () => ScreenReturnProps = () => {
  const context = useContext(screensContext);
  if (!context) throw "useScreens must be used within a ScreensProvider";
  return context;
};
